import { OtherAppsPage } from "./OtherAppsPage";
import {
  Activity,
  AlertTriangle,
  ArrowDownToLine,
  BarChart3,
  CheckCircle2,
  Database,
  FileDown,
  FileUp,
  Gauge,
  Globe2,
  Link2,
  ListChecks,
  Megaphone,
  Radio,
  Search,
  Settings,
  ShieldAlert,
  Store,
  Users
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";

declare global {
  interface Window {
    shopify?: {
      idToken?: () => Promise<string>;
    };
  }
}

type Page =
  | "Dashboard"
  | "Backlinks"
  | "Referring Domains"
  | "Lost Links"
  | "Toxic Links"
  | "Competitors"
  | "Mentions"
  | "Affiliates"
  | "Imports"
  | "Sync"
  | "Exports"
  | "Settings"
  | "Billing"
  | "Logs"
  | "Other apps";

type ApiState<T> = { loading: boolean; data?: T; error?: string };

interface DashboardData {
  ok: boolean;
  shop: { domain: string; source: string };
  metrics: Record<string, string | number | { backlinks_tracked: number; max_backlinks_tracked: number }>;
  recent_backlinks: Record<string, unknown>[];
  activity: Record<string, unknown>[];
}

interface ListData {
  ok: boolean;
  page: number;
  limit: number;
  total: number;
  rows: Record<string, unknown>[];
}

const nav: Array<{ page: Page; icon: typeof Gauge }> = [
  { page: "Dashboard", icon: Gauge },
  { page: "Backlinks", icon: Link2 },
  { page: "Referring Domains", icon: Globe2 },
  { page: "Lost Links", icon: ArrowDownToLine },
  { page: "Toxic Links", icon: ShieldAlert },
  { page: "Competitors", icon: Users },
  { page: "Mentions", icon: Megaphone },
  { page: "Affiliates", icon: Radio },
  { page: "Imports", icon: FileUp },
  { page: "Sync", icon: Activity },
  { page: "Exports", icon: FileDown },
  { page: "Settings", icon: Settings },
  { page: "Billing", icon: Store },
  { page: "Logs", icon: ListChecks },
  { page: "Other apps", icon: Activity }
];

function pathSegmentForPage(page: Page): string {
  if (page === "Dashboard") return "";
  if (page === "Other apps") return "apps";
  return page.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function routeForPage(page: Page): string {
  const path = page === "Dashboard" ? "/" : `/${pathSegmentForPage(page)}`;
  const params = new URLSearchParams();
  const current = new URLSearchParams(window.location.search);
  ["shop", "host", "id_token"].forEach((key) => {
    const value = current.get(key);
    if (value) params.set(key, value);
  });
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

function pageFromLocation(pathname = window.location.pathname, search = window.location.search): Page {
  const params = new URLSearchParams(search);
  const explicit = params.get("page");
  if (explicit) {
    const fromQuery = nav.find((item) => item.page.toLowerCase() === explicit.toLowerCase() || pathSegmentForPage(item.page) === explicit)?.page;
    if (fromQuery) return fromQuery;
  }
  const segment = pathname.split("/").filter(Boolean).pop() || "";
  return nav.find((item) => pathSegmentForPage(item.page) === segment)?.page || "Dashboard";
}

function AppBridgeNav({ currentPage, onSelect }: { currentPage: Page; onSelect: (page: Page) => void }) {
  return React.createElement(
    "s-app-nav",
    null,
    nav.map((item) =>
      React.createElement(
        "s-link",
        {
          key: item.page,
          href: routeForPage(item.page),
          rel: item.page === "Dashboard" ? "home" : undefined,
          "aria-current": currentPage === item.page ? "page" : undefined,
          onClick: (event: React.MouseEvent) => {
            event.preventDefault();
            onSelect(item.page);
          }
        },
        item.page
      )
    )
  );
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("content-type", "application/json");
  const requestUrl = new URL(path, window.location.origin);
  const currentShop = new URLSearchParams(window.location.search).get("shop");
  if (currentShop && !requestUrl.searchParams.has("shop")) requestUrl.searchParams.set("shop", currentShop);
  try {
    const token = await window.shopify?.idToken?.();
    if (token) headers.set("authorization", `Bearer ${token}`);
  } catch {
    // The app also supports review fallback mode outside an embedded frame.
  }
  const response = await fetch(`${requestUrl.pathname}${requestUrl.search}`, { ...init, headers, credentials: "include" });
  const payload = (await response.json()) as T & { error?: string };
  if (!response.ok) throw new Error(payload.error ?? `Request failed: ${response.status}`);
  return payload;
}

function metricLabel(key: string): string {
  return key.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function valueText(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "object") return Object.values(value as Record<string, unknown>).join(" / ");
  return String(value);
}

function StatusPill({ value }: { value: unknown }) {
  const text = String(value ?? "open");
  return <span className={`pill pill-${text.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>{text}</span>;
}

function DataTable({ rows, columns }: { rows: Record<string, unknown>[]; columns: string[] }) {
  const minWidth = Math.max(900, columns.length * 155);
  return (
    <div className="table-shell">
      <table style={{ minWidth }}>
        <thead>
          <tr>{columns.map((column) => <th key={column}>{metricLabel(column)}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="empty">No rows yet</td>
            </tr>
          ) : rows.map((row, index) => (
            <tr key={String(row.id ?? index)}>
              {columns.map((column) => (
                <td key={column}>{column.includes("status") || column === "link_type" ? <StatusPill value={row[column]} /> : valueText(row[column])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function useList(path: string, refresh = 0): ApiState<ListData> {
  const [state, setState] = useState<ApiState<ListData>>({ loading: true });
  useEffect(() => {
    let active = true;
    setState({ loading: true });
    api<ListData>(path).then((data) => active && setState({ loading: false, data })).catch((error) => active && setState({ loading: false, error: error.message }));
    return () => {
      active = false;
    };
  }, [path, refresh]);
  return state;
}

function Dashboard({ refresh, onAction }: { refresh: number; onAction: (page: Page) => void }) {
  const [state, setState] = useState<ApiState<DashboardData>>({ loading: true });
  useEffect(() => {
    let active = true;
    async function load() {
      await api("/api/session");
      await api("/api/events", { method: "POST", body: JSON.stringify({ event: "app.loaded" }) });
      const data = await api<DashboardData>("/api/dashboard");
      if (active) setState({ loading: false, data });
    }
    load().catch((error) => active && setState({ loading: false, error: error.message }));
    return () => {
      active = false;
    };
  }, [refresh]);
  if (state.loading) return <Loading title="Loading dashboard" />;
  if (state.error) return <ErrorBox message={state.error} />;
  const metrics = state.data?.metrics ?? {};
  const keys = [
    "total_backlinks",
    "new_backlinks_7d",
    "lost_backlinks_7d",
    "referring_domains",
    "toxic_links",
    "pr_mentions",
    "influencer_mentions",
    "affiliate_links",
    "competitor_gap_opportunities",
    "authority_trend",
    "provider_status",
    "quota_usage"
  ];
  return (
    <main className="page">
      <header className="page-head">
        <div>
          <h1>Backlink intelligence for commerce brands.</h1>
          <p>Track backlinks, mentions, authority signals, and competitor gaps without a heavy SEO suite.</p>
        </div>
        <StatusPill value={metrics.provider_status} />
      </header>
      <section className="actions">
        <button onClick={() => onAction("Imports")}><FileUp size={16} /> Import backlinks CSV</button>
        <button onClick={() => onAction("Backlinks")}><Link2 size={16} /> Add referring URL</button>
        <button onClick={() => onAction("Sync")}><Activity size={16} /> Run verification</button>
        <button onClick={() => onAction("Competitors")}><Users size={16} /> Add competitor</button>
        <button onClick={() => onAction("Exports")}><FileDown size={16} /> Export report</button>
      </section>
      <section className="metric-grid">
        {keys.map((key) => (
          <div className="metric" key={key}>
            <span>{metricLabel(key)}</span>
            <strong>{valueText(metrics[key])}</strong>
          </div>
        ))}
      </section>
      <section className="split">
        <Panel title="New and recent backlinks">
          <DataTable rows={state.data?.recent_backlinks ?? []} columns={["status", "referring_domain", "target_url", "anchor_text", "toxic_score", "authority_score", "link_type"]} />
        </Panel>
        <Panel title="Verification health">
          <ul className="activity-list">
            {(state.data?.activity ?? []).map((item, index) => <li key={index}><span>{valueText(item.event)}</span><small>{valueText(item.message)}</small></li>)}
          </ul>
        </Panel>
      </section>
    </main>
  );
}

function BacklinksPage({ onRefresh }: { onRefresh: () => void }) {
  const [form, setForm] = useState({ referring_url: "https://examplepress.com/guides/sustainable-cotton", target_url: "https://review-shop.com/products/cotton-hoodie", anchor: "sustainable cotton hoodie" });
  const [notice, setNotice] = useState("");
  const list = useList("/api/backlinks", notice.length);
  async function submit() {
    const result = await api<{ id: string }>("/api/backlinks/manual", { method: "POST", body: JSON.stringify(form) });
    setNotice(`Added backlink ${result.id}`);
    onRefresh();
  }
  return (
    <main className="page">
      <PageTitle title="Backlinks" description="Search, classify, verify, and export tracked referring URLs." />
      <Panel title="Add referring URL">
        <div className="form-grid">
          <input value={form.referring_url} onChange={(event) => setForm({ ...form, referring_url: event.target.value })} aria-label="Referring URL" />
          <input value={form.target_url} onChange={(event) => setForm({ ...form, target_url: event.target.value })} aria-label="Target URL" />
          <input value={form.anchor} onChange={(event) => setForm({ ...form, anchor: event.target.value })} aria-label="Anchor text" />
          <button onClick={submit}><CheckCircle2 size={16} /> Add URL</button>
        </div>
        {notice && <p className="notice">{notice}</p>}
      </Panel>
      <DataTable rows={list.data?.rows ?? []} columns={["status", "referring_url", "referring_domain", "target_url", "anchor_text", "link_rel", "first_seen_at", "last_seen_at", "last_verified_at", "latest_http_status", "authority_score", "toxic_score", "link_type"]} />
    </main>
  );
}

function ImportsPage({ onRefresh }: { onRefresh: () => void }) {
  const sample = "referring_url,target_url,anchor,authority,rel\nhttps://examplepress.com/guides/sustainable-cotton,https://review-shop.com/products/cotton-hoodie,sustainable cotton hoodie,62,follow\nhttps://coupon-example.com/deals/linkpulse?aff=demo,https://review-shop.com/,discount code,31,nofollow sponsored";
  const [csv, setCsv] = useState(sample);
  const [notice, setNotice] = useState("");
  async function submit() {
    const result = await api<{ imported_rows: number; skipped_rows: number }>("/api/imports/backlinks", { method: "POST", body: JSON.stringify({ csv }) });
    setNotice(`Imported ${result.imported_rows} rows; skipped ${result.skipped_rows}.`);
    onRefresh();
  }
  const jobs = useList("/api/imports", notice.length);
  return (
    <main className="page">
      <PageTitle title="Imports" description="Import backlink CSVs, competitor exports, referring domains, manual URL lists, or provider raw exports." />
      <Panel title="Backlink CSV mapper">
        <textarea value={csv} onChange={(event) => setCsv(event.target.value)} rows={8} />
        <div className="actions compact"><button onClick={submit}><FileUp size={16} /> Import CSV</button></div>
        {notice && <p className="notice">{notice}</p>}
      </Panel>
      <DataTable rows={jobs.data?.rows ?? []} columns={["source_type", "status", "total_rows", "processed_rows", "imported_rows", "skipped_rows", "created_at", "completed_at"]} />
    </main>
  );
}

function CompetitorsPage({ onRefresh }: { onRefresh: () => void }) {
  const [domain, setDomain] = useState("competitor-example.com");
  const [notice, setNotice] = useState("");
  const competitors = useList("/api/competitors", notice.length);
  const gaps = useList("/api/gap", notice.length);
  async function add() {
    await api("/api/competitors", { method: "POST", body: JSON.stringify({ domain }) });
    setNotice(`Added ${domain}`);
    onRefresh();
  }
  return (
    <main className="page">
      <PageTitle title="Competitors" description="Track domains, import competitor CSVs, and organize backlink gap opportunities." />
      <Panel title="Add competitor">
        <div className="form-grid two">
          <input value={domain} onChange={(event) => setDomain(event.target.value)} aria-label="Competitor domain" />
          <button onClick={add}><Users size={16} /> Add competitor</button>
        </div>
        {notice && <p className="notice">{notice}</p>}
      </Panel>
      <Panel title="Competitor domains">
        <DataTable rows={competitors.data?.rows ?? []} columns={["domain", "label", "source", "active", "last_sync_at", "updated_at"]} />
      </Panel>
      <Panel title="Gap opportunities">
        <DataTable rows={gaps.data?.rows ?? []} columns={["referring_domain", "competitors_count", "authority_score", "toxic_score", "gap_score", "opportunity_type", "status"]} />
      </Panel>
    </main>
  );
}

function SyncPage() {
  const [notice, setNotice] = useState("");
  const [polling, setPolling] = useState(false);
  const [tick, setTick] = useState(0);
  const jobs = useList("/api/sync/history?limit=20", tick);
  const hasActive = useMemo(() => (jobs.data?.rows ?? []).some((row) => ["queued", "running"].includes(String(row.status))), [jobs.data]);
  useEffect(() => {
    if (!hasActive) {
      setPolling(false);
      return;
    }
    setPolling(true);
    const timer = window.setInterval(() => setTick((value) => value + 1), 5000);
    return () => window.clearInterval(timer);
  }, [hasActive]);
  async function run(type: string) {
    const result = await api<{ job_id: string }>("/api/sync/start", { method: "POST", body: JSON.stringify({ type }) });
    await api("/api/events", { method: "POST", body: JSON.stringify({ event: "ui.background_poll_started" }) });
    setNotice(`Queued ${type}: ${result.job_id}`);
    setTick((value) => value + 1);
  }
  useEffect(() => {
    if (!polling) void api("/api/events", { method: "POST", body: JSON.stringify({ event: "ui.background_poll_stopped" }) }).catch(() => undefined);
  }, [polling]);
  return (
    <main className="page">
      <PageTitle title="Sync" description="Run target URL sync, backlink verification, provider sync, competitor refresh, and stuck job rescue." />
      <section className="actions">
        <button onClick={() => run("backlink_verify")}><Activity size={16} /> Run verification</button>
        <button onClick={() => api("/api/targets/sync", { method: "POST", body: "{}" }).then(() => setNotice("Store target URL sync completed"))}><Database size={16} /> Sync store URLs</button>
      </section>
      {notice && <p className="notice">{notice}</p>}
      <DataTable rows={jobs.data?.rows ?? []} columns={["type", "status", "total_items", "processed_items", "progress_percent", "progress_message", "error_message", "created_at", "completed_at"]} />
    </main>
  );
}

function ExportsPage() {
  const [notice, setNotice] = useState("");
  const jobs = useList("/api/exports", notice.length);
  async function create() {
    const result = await api<{ job_id: string; row_count: number }>("/api/exports/backlinks", { method: "POST", body: "{}" });
    setNotice(`Created export ${result.job_id} with ${result.row_count} rows.`);
  }
  return (
    <main className="page">
      <PageTitle title="Exports" description="Create CSV exports for backlinks, lost links, toxic review lists, competitor gaps, mentions, affiliates, and crawl failures." />
      <section className="actions compact"><button onClick={create}><FileDown size={16} /> Export backlinks CSV</button></section>
      {notice && <p className="notice">{notice}</p>}
      <DataTable rows={jobs.data?.rows ?? []} columns={["export_type", "status", "row_count", "file_key", "created_at", "completed_at"]} />
    </main>
  );
}

function GenericListPage({ title, description, path, columns }: { title: string; description: string; path: string; columns: string[] }) {
  const list = useList(path);
  return (
    <main className="page">
      <PageTitle title={title} description={description} />
      <div className="toolbar">
        <Search size={16} />
        <input placeholder="Search" aria-label={`Search ${title}`} />
      </div>
      {list.error ? <ErrorBox message={list.error} /> : <DataTable rows={list.data?.rows ?? []} columns={columns} />}
    </main>
  );
}

function SettingsPage() {
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState({ canonical_store_domain: "review-shop.com", provider_type: "manual_csv", toxic_score_threshold: 70, lost_link_grace_days: 2 });
  async function save() {
    await api("/api/settings", { method: "POST", body: JSON.stringify(form) });
    setNotice("Settings saved");
  }
  return (
    <main className="page">
      <PageTitle title="Settings" description="Configure store domains, brand terms, competitors, provider state, crawl rules, risk thresholds, retention, quotas, and privacy behavior." />
      <Panel title="Store and crawl settings">
        <div className="form-grid">
          <label>Canonical store domain<input value={form.canonical_store_domain} onChange={(event) => setForm({ ...form, canonical_store_domain: event.target.value })} /></label>
          <label>Provider type<input value={form.provider_type} onChange={(event) => setForm({ ...form, provider_type: event.target.value })} /></label>
          <label>Toxic threshold<input type="number" value={form.toxic_score_threshold} onChange={(event) => setForm({ ...form, toxic_score_threshold: Number(event.target.value) })} /></label>
          <label>Lost link grace days<input type="number" value={form.lost_link_grace_days} onChange={(event) => setForm({ ...form, lost_link_grace_days: Number(event.target.value) })} /></label>
          <button onClick={save}><Settings size={16} /> Save settings</button>
        </div>
        {notice && <p className="notice">{notice}</p>}
      </Panel>
    </main>
  );
}

function BillingPage() {
  const [state, setState] = useState<ApiState<Record<string, unknown>>>({ loading: true });
  useEffect(() => {
    api<Record<string, unknown>>("/api/billing").then((data) => setState({ loading: false, data })).catch((error) => setState({ loading: false, error: error.message }));
  }, []);
  const pricing = state.data?.pricing as Record<string, unknown> | undefined;
  const subscription = state.data?.subscription as Record<string, unknown> | undefined;
  const planUrl = String(pricing?.plan_selection_url ?? subscription?.confirmation_url ?? "");
  function preventEmptyPlanLink(event: React.MouseEvent<HTMLAnchorElement>) {
    if (planUrl) return;
    event.preventDefault();
  }
  return (
    <main className="page">
      <PageTitle title="Billing" description="Review the current billing plan, pricing currency, quota limits, and plan selection state." />
      {state.error && <ErrorBox message={state.error} />}
      <Panel title="Plan selection">
        <div className="billing-row">
          <div>
            <strong>{valueText(subscription?.plan_name) || "No active plan selected"}</strong>
            <p>Open the hosted plan selection page to accept, decline, or change the app subscription, then return here to refresh billing status.</p>
            {planUrl && <p className="tiny">Shopify-hosted billing URL is ready and opens in the top-level admin window.</p>}
          </div>
          <a className="billing-link" href={planUrl || "#"} target="_top" rel="noopener" aria-disabled={!planUrl} onClick={preventEmptyPlanLink}>
            <Store size={16} /> Change plan
          </a>
        </div>
      </Panel>
      <Panel title="Billing approval check">
        <p>Use the Change plan button above to open Shopify's hosted plan selection page. This is a direct top-level link, so it works inside Shopify Admin without an async popup or blocked iframe navigation.</p>
        <p><a href="/review/billing-proof" target="_blank" rel="noopener">Open billing resolution proof</a></p>
      </Panel>
      <div className="metric-grid">
        <div className="metric"><span>Basic Monthly</span><strong>{valueText(pricing?.currency)} 5/month</strong></div>
        <div className="metric"><span>Basic Yearly</span><strong>{valueText(pricing?.currency)} 49/year</strong></div>
        <div className="metric"><span>Provider sync</span><strong>Optional</strong></div>
        <div className="metric"><span>Tracked backlinks</span><strong>2,000</strong></div>
      </div>
    </main>
  );
}

function PageTitle({ title, description }: { title: string; description: string }) {
  return (
    <header className="page-head tight">
      <div>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
    </header>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="panel">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function Loading({ title }: { title: string }) {
  return <main className="page"><div className="loading">{title}</div></main>;
}

function ErrorBox({ message }: { message: string }) {
  return <div className="error"><AlertTriangle size={18} /> {message}</div>;
}

export default function App() {
  const [page, setPage] = useState<Page>(() => pageFromLocation());
  const [refresh, setRefresh] = useState(0);
  const navigatePage = React.useCallback((nextPage: Page) => {
    setPage(nextPage);
    const nextUrl = routeForPage(nextPage);
    if (`${window.location.pathname}${window.location.search}` !== nextUrl) {
      window.history.pushState({}, "", nextUrl);
    }
  }, []);

  useEffect(() => {
    const syncFromLocation = () => setPage(pageFromLocation());
    const syncFromShopifyNavigation = (event: Event) => {
      const href = (event as CustomEvent<{ href?: string }>).detail?.href;
      if (!href) return;
      const url = new URL(href, window.location.origin);
      setPage(pageFromLocation(url.pathname, url.search));
    };
    window.addEventListener("popstate", syncFromLocation);
    window.addEventListener("shopify:navigate", syncFromShopifyNavigation);
    document.addEventListener("shopify:navigate", syncFromShopifyNavigation);
    return () => {
      window.removeEventListener("popstate", syncFromLocation);
      window.removeEventListener("shopify:navigate", syncFromShopifyNavigation);
      document.removeEventListener("shopify:navigate", syncFromShopifyNavigation);
    };
  }, []);

  function bump() {
    setRefresh((value) => value + 1);
  }
  return (
    <>
    <AppBridgeNav currentPage={page} onSelect={navigatePage} />
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <BarChart3 size={24} />
          <div><strong>LinkPulse Radar</strong><span>Store backlink intelligence</span></div>
        </div>
        <nav>
          {nav.map(({ page: item, icon: Icon }) => (
            <button key={item} className={page === item ? "active" : ""} onClick={() => navigatePage(item)}>
              <Icon size={17} />
              <span>{item}</span>
            </button>
          ))}
        </nav>
      </aside>
      {page === "Dashboard" && <Dashboard refresh={refresh} onAction={navigatePage} />}
      {page === "Backlinks" && <BacklinksPage onRefresh={bump} />}
      {page === "Referring Domains" && <GenericListPage title="Referring Domains" description="Review referring domain growth, authority proxy, toxic averages, and PR/influencer/affiliate flags." path="/api/referring-domains" columns={["domain", "root_domain", "backlinks_count", "live_backlinks_count", "lost_backlinks_count", "authority_score", "authority_source", "toxic_avg", "pr_count", "influencer_count", "affiliate_count"]} />}
      {page === "Lost Links" && <GenericListPage title="Lost Links" description="Links that were live before and now need recheck, recovery notes, or ignore decisions." path="/api/lost-links" columns={["referring_url", "referring_domain", "target_url", "anchor_text", "last_seen_at", "lost_at", "latest_http_status", "authority_score", "toxic_score"]} />}
      {page === "Toxic Links" && <GenericListPage title="Toxic Links" description="Heuristic risk scoring only. Review links before taking action." path="/api/toxic-links" columns={["referring_url", "referring_domain", "target_url", "anchor_text", "toxic_score", "toxic_factors_json", "authority_score", "status", "last_verified_at", "reviewed_state"]} />}
      {page === "Competitors" && <CompetitorsPage onRefresh={bump} />}
      {page === "Mentions" && <GenericListPage title="Mentions" description="Track PR, brand, product, founder, domain, influencer, and affiliate mention URLs." path="/api/mentions" columns={["mention_url", "domain", "mention_type", "linked", "target_url", "brand_term", "page_title", "authority_score", "pr_score", "status"]} />}
      {page === "Affiliates" && <GenericListPage title="Affiliates" description="Detect affiliate-looking parameters, coupon/review domains, sponsored rel attributes, and campaign values." path="/api/affiliates" columns={["referring_url", "referring_domain", "target_url", "detected_param", "detected_value", "signal_score", "status"]} />}
      {page === "Imports" && <ImportsPage onRefresh={bump} />}
      {page === "Sync" && <SyncPage />}
      {page === "Exports" && <ExportsPage />}
      {page === "Settings" && <SettingsPage />}
      {page === "Billing" && <BillingPage />}
      {page === "Logs" && <GenericListPage title="Logs" description="Operational event log for app load, OAuth, billing, imports, verification, provider state, webhooks, privacy, and exports." path="/api/logs" columns={["level", "event", "message", "created_at"]} />}
      {page === "Other apps" && <OtherAppsPage sourceId="368980033537" />}
    </div>
    </>
  );
}
